<template>
  <page-template :show-loader="false">
    <template #pageHeadline>
      {{ $t('dashboard.pageHeadline') }}
    </template>
    <template #pageHeadlineRight>
      <div class="rbz-field server-group">
        <rbz-dropdown
          :items="serverGroupItems"
          type="menu"
          classes="server-group-dropdown"
          :placeholder="$t('eventsLog.serverGroupDpPlacehoder')"
          :value="selectedServerGroup"
          :can-clear-single-select="true"
          @update:value="addServerGroupToFilter"
        >
        </rbz-dropdown>
      </div>
    </template>
    <template #secondLevelHeaderEntire>
      <rbz-filter
        ref="rbzFilter"
        :search-disabled="isTimelineLoading || isStatsLoading || isTopxLoading"
        :default-time="defaultTime"
        :query-history="queryHistory"
        :is-query-history-loading="isQueryHistoryLoading"
        :show-query-history="true"
        @blur-filter="onBlurFilterInput"
        @clear-filter="clearSelectedServerGroups"
        @search="loadData"
      >
        <template #additionalButtons>
          <rbz-form-button
            variant="tertiary"
            icon="IconLink"
            :label="$t('dashboard.openEventsLog')"
            link-route-name="Events Log"
            data-qa="events-log-link-button"
            :link-query="getQuery()"
          />
        </template>
      </rbz-filter>
    </template>
    <template #pageContent>
      <div v-show="actionRequired">
        <div class="rbz-card light-gray is-fullwidth">
          <div class="action-required-panel rbz-text-large">
            {{ $t('dashboard.actionRequired') }}
          </div>
        </div>
      </div>
      <div v-show="!actionRequired">
        <div class="charts">
          <rbz-dashboard-charts
            :timeline="timeline"
            :is-timeline-loading="isTimelineLoading"
            :stats="stats"
            :is-stats-loading="isStatsLoading"
            @zoomed="chartsZoomed"
          />
        </div>
        <div class="tables">
          <rbz-dashboard-tables
            :data="topx"
            :is-topx-loading="isTopxLoading"
            @add-filter="addFilter"
          />
        </div>
      </div>
      <rbz-modal-dialog
        :show="showOnboardingWizardModal"
        @closeModal="closeOnboardingWizardModal"
        size="wizard"
        :hide-footer="true"
      >
        <template #content>
          <div class="wizard">
            <iframe
              :src="onboardingWizardUrl"
              allow="
                clipboard-write;
                fullscreen;
                autoplay;
                encrypted-media;
                accelerometer;
                gyroscope;
                web-share;
              "
            ></iframe>
          </div>
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue';
import RequestsUtils from '@/helpers/RequestsUtils';
import RbzFilter from '@/components/RbzFilter.vue';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import RbzDashboardCharts from '@/components/RbzDashboardCharts.vue';
import RbzDashboardTables from '@/components/RbzDashboardTables.vue';
import RbzFormButton from '@/components/RbzFormButton.vue';
import { DashboardTableEntry, GenericObject, ServerGroup } from '@/types';
import axios, { AxiosError, AxiosResponse } from 'axios';
import Utils from 'ui-component-library/helpers/Utils';
import _ from 'lodash';
import PageTemplate from '@/views/PageTemplate.vue';
import { RouteParams } from 'vue-router';
import configuration from '@/configuration/dashboard.json';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { UserAPIKey } from '@/types';
import { ENV } from '@/constants';
import { shouldUseMockData, getDashboardMockData } from '@/helpers/MockDataUtils';

export default defineComponent({
  name: 'RbzDashboard',
  components: {
    PageTemplate,
    RbzFormButton,
    RbzDashboardCharts,
    RbzDashboardTables,
    RbzFilter,
    RbzModalDialog,
  },
  data() {
    return {
      actionRequired: true,

      cancelSource: axios.CancelToken.source(),
      timeline: [] as GenericObject[],
      isTimelineLoading: false,
      stats: [] as GenericObject[],
      isStatsLoading: false,
      topx: [] as DashboardTableEntry[],
      isTopxLoading: false,
      queryHistory: [] as string[],
      isQueryHistoryLoading: false,
      pageLoadingCounter: null,
      // ServerGroup
      selectedServerGroup: null,
      serverGroups: null as ServerGroup[],
      // Onboarding Wizard
      onboardingWizardUrl: '',
      onboardingWizardBaseUrl: 'https://start.link11.com/',
      showOnboardingWizardModal: false,
      accountId: null,
      apiKeys: null as UserAPIKey[],
    };
  },
  watch: {
    $route: {
      handler: async function (val, oldVal) {
        if (val.name.includes('Dashboard')) {
          if (oldVal?.name?.includes('Dashboard') && !val.query?.filters) {
            this.timeline = [];
            this.stats = [];
            this.topx = [];
            this.actionRequired = true;
            nextTick(() => {
              if (
                this.$refs.rbzFilter &&
                typeof this.$refs.rbzFilter.setDataFromRoute === 'function'
              ) {
                this.$refs.rbzFilter.setDataFromRoute();
              }
            });
          }
          if (!oldVal?.name?.includes('Dashboard') && !val.query?.filters) {
            this.actionRequired = false;
            this.isTimelineLoading = true;
            this.isStatsLoading = true;
            this.isTopxLoading = true;
            nextTick(() => {
              if (
                this.$refs.rbzFilter &&
                typeof this.$refs.rbzFilter.setDataFromRoute === 'function'
              ) {
                this.$refs.rbzFilter.search(true, false);
              }
            });
          }
        }
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    dataExists(): boolean {
      return this.timeline.length > 0 || this.stats.length > 0 || this.topx.length > 0;
    },

    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    defaultTime(): number {
      return configuration.defaultTime;
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    serverGroupItems() {
      if (!this.serverGroups) {
        return [];
      }
      return this.serverGroups.map((serverGroup) => [serverGroup.name, serverGroup.name]);
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    async loadData(query: string, saveHistory: boolean = true) {
      this.timeline = [];
      this.stats = [];
      this.topx = [];
      this.actionRequired = false;
      this.isTimelineLoading = true;
      this.isStatsLoading = true;
      this.isTopxLoading = true;

      // Use mock data in development/localhost environment
      if (shouldUseMockData()) {
        const mockData = getDashboardMockData();
        this.timeline = mockData.timeline || [];
        this.stats = mockData.stats || [];
        this.topx = (mockData.topx || []) as DashboardTableEntry[];
        this.isTimelineLoading = false;
        this.isStatsLoading = false;
        this.isTopxLoading = false;
        return;
      }

      await this.branchesStore.getBranches();
      query += query ? ', ' : '';
      query += `branch=${this.selectedBranch}`;
      this.cancelSource.cancel('Operation cancelled because a new search has began');
      this.cancelSource = axios.CancelToken.source();
      const config = {
        headers: {
          provider: ENV.VITE_LOGS_DB_PROVIDER,
          syntax: 'string_query',
        },
        cancelToken: this.cancelSource.token,
      };
      let requestFailed = false;
      const onFail = (error: AxiosError) => {
        if (!requestFailed) {
          requestFailed = true;
          Utils.toast({
            message: error.response?.data?.detail || '',
            type: 'is-danger',
          });
        }
      };
      RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `data/timeline?filters=${encodeURIComponent(query)}`,
        config: config,
        onFail: onFail,
      })
        .then((response: AxiosResponse) => {
          this.timeline = response?.data?.data?.results || [];
        })
        .finally(() => {
          this.isTimelineLoading = false;
        });
      RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `data/stats?filters=${encodeURIComponent(query)}`,
        config: config,
        onFail: onFail,
      })
        .then((response: AxiosResponse) => {
          this.stats = response?.data?.data?.results || [];
        })
        .finally(() => {
          this.isStatsLoading = false;
        });
      RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `data/topx?save_history=${saveHistory}&filters=${encodeURIComponent(query)}`,
        config: config,
        onFail: onFail,
      })
        .then((response: AxiosResponse) => {
          this.topx = response?.data?.data?.results || [];
          this.loadQueryHistory();
        })
        .finally(() => {
          this.isTopxLoading = false;
        });
    },

    onBlurFilterInput(inputValue: string) {
      this.updateServerGroupDropdown(inputValue);
    },

    clearSelectedServerGroups() {
      this.selectedServerGroup = null;
    },

    updateServerGroupDropdown(inputValue: string) {
      const filters = inputValue.split(/, /);
      const serverGroupFilter = filters.findLast((filter) =>
        filter.trim().startsWith('server_group=')
      );
      if (serverGroupFilter) {
        const serverGroupValue = serverGroupFilter
          .trim()
          .split('=')[1]
          ?.trim()
          .replace(/^"|"$/g, '');
        const selectedServerGroup = this.serverGroups.find(
          (group) => group.name === serverGroupValue.trim()
        );
        this.selectedServerGroup = selectedServerGroup ? selectedServerGroup.name : null;
      } else {
        this.selectedServerGroup = null;
      }
    },

    addServerGroupToFilter(host: string) {
      this.selectedServerGroup = host;
      const filterString = 'server_group="' + host + '"';
      if (this.$refs.rbzFilter && typeof this.$refs.rbzFilter.addServerGroupFilter === 'function') {
        this.$refs.rbzFilter.addServerGroupFilter(filterString);
      }
    },

    async loadQueryHistory() {
      this.isQueryHistoryLoading = true;
      const queryHistoryResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `data/query_history?query_type=dashboard`,
        config: {
          headers: {
            accept: 'application/json',
            provider: ENV.VITE_STATE_DB_PROVIDER,
          },
        },
      });
      this.queryHistory = queryHistoryResponse?.data?.data?.results || [];
      this.isQueryHistoryLoading = false;
    },

    async loadServerGroups() {
      const url = `conf/${this.selectedBranch}/server-groups`;
      const serverGroupsResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
      });
      this.serverGroups = serverGroupsResponse?.data?.items || [];
      if (this.$route.query?.filters) {
        let filters = this.$route.query?.filters.toString();
        setTimeout(() => {
          this.updateServerGroupDropdown(filters);
        }, 400);
      }
    },

    chartsZoomed(newBounds: { min: number; max: number }) {
      if (!_.isFinite(newBounds.min) || !_.isFinite(newBounds.max)) {
        return;
      }
      const minDate = new Date(newBounds.min).toISOString();
      const maxDate = new Date(newBounds.max).toISOString();
      this.$refs.rbzFilter.changeTime([minDate, maxDate]);
    },

    addFilter(filterToAdd: string) {
      this.$refs.rbzFilter.addFilter(filterToAdd);
    },

    getQuery(): RouteParams {
      const query = this.$route.query?.filters?.toString();
      return { filters: query };
    },

    async loadAPIKeys() {
      const url = `accounts/${this.accountId}/api-keys`;
      const method = 'GET';
      const response = await RequestsUtils.sendRequest({
        methodName: method,
        url,
      });
      this.apiKeys = response?.data?.items || [];
    },

    async addAPIKey(newAPIKey: UserAPIKey) {
      const url = `accounts/${this.accountId}/api-keys`;
      const methodName = 'POST';
      await RequestsUtils.sendRequest({
        methodName,
        url,
        data: { title: newAPIKey.title },
      });
      await this.loadAPIKeys();
    },

    async checkOnboardingWizard() {
      // show onboarding wizard only if default configuration was not changed or if there is no server group
      if (
        this.serverGroups.length < 1 ||
        !this.serverGroups.some((server) =>
          server.server_names.some((name) => name !== 'default.site')
        )
      ) {
        this.accountId = this.userStore.getCookieAccountId() || '';
        const profileProvider = this.userStore.getCookieProfileProvider() || '';

        if (
          this.accountId &&
          profileProvider === 'reblaze' &&
          this.userStore.checkAccessLevel(this.userStore.accessLevels.waapConfigEditor)
        ) {
          await this.loadAPIKeys();
          // get api key with title "Onboarding", if not existing, create one
          const apiKeyTitle = 'Onboarding';
          let onboardingAPIKey = this.apiKeys.find((item) => item.title === apiKeyTitle);

          if (!onboardingAPIKey?.key) {
            const newApiKey: UserAPIKey = {
              account_id: '',
              created_by: '',
              created_time: null,
              email: '',
              id: '',
              key: '',
              title: apiKeyTitle,
            };
            await this.addAPIKey(newApiKey);
            onboardingAPIKey = this.apiKeys.find((item) => item.title === apiKeyTitle);
          }

          if (onboardingAPIKey?.key) {
            const subdomain = window.location.hostname.split('.app.reblaze.io')[0]; // Onboarding Wizard combines subdomain with '.app.reblaze.io' for communcation with api
            const url = new URL(this.onboardingWizardBaseUrl);
            url.searchParams.set('apikey', onboardingAPIKey.key);
            url.searchParams.set('acct', subdomain);
            this.onboardingWizardUrl = url.toString();
            this.showOnboardingWizardModal = true;
          }
        }
      }
    },

    closeOnboardingWizardModal() {
      this.showOnboardingWizardModal = false;
    },

    handleIframeMessage(event) {
      const message = event.data;
      if (message.type === 'closeOnboardingWizard') {
        this.closeOnboardingWizardModal();
      }
    },
  },
  async mounted() {
    this.setPageLoadingStatus(true);
    const loadQueryPromise = this.loadQueryHistory();
    const serverGroupPromise = this.loadServerGroups();
    await Promise.all([await loadQueryPromise, await serverGroupPromise]);
    this.checkOnboardingWizard();
    this.setPageLoadingStatus(false);
    window.addEventListener('message', this.handleIframeMessage);
  },
});
</script>
<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

:deep(.rbz-page-header .server-group) {
  .dropdown-header {
    max-width: 190px;
    background-color: map.get(colors.$colors, 'mountain-meadow-light');
    color: map.get(colors.$colors, 'white');
  }

  .dropdown-header.open {
    border-radius: 4px;
    border: 0;
  }

  .dropdown-header > .tags-wrapper > .custom-tag {
    background-color: map.get(colors.$colors, 'mountain-meadow-light');
    color: map.get(colors.$colors, 'white');
  }

  .tag-content {
    font-size: 14px;
  }

  .custom-tag > .remove-button {
    display: none;
  }

  .dropdown-header > .scrollbox-shadowed {
    background: map.get(colors.$colors, 'mountain-meadow-light');
  }

  .dropdown-placeholder {
    color: map.get(colors.$colors, 'white');
    max-width: 135px;
  }

  .expand-icon-wrapper > .chevron {
    fill: map.get(colors.$colors, 'white');
  }

  .dropdown-list {
    top: 40px;
    border-radius: 4px;
    border-top-width: 1px;
    width: 416px;
    right: 0;
  }
}

.action-required-panel {
  padding: 10px 15px;
  text-align: center;
}

.wizard {
  width: 100%;
  height: 795px;
  margin: 0 auto;

  iframe {
    display: block;
    width: 100%;
    height: 100%;
  }

  @media (height <= 865px) {
    height: calc(100vh - 80px);

    iframe {
      max-width: 916px;
      margin: 0 auto;
    }
  }
}
</style>
